// src/hooks/dashboard/useDashboard.ts
import { useEffect, useMemo, useState } from "react";
import type { Layout, Layouts } from "react-grid-layout";
import type { Breakpoint, DashboardWidget } from "../../types/dashboard/dashboard.types";
import {
  buildLayoutsFromWidgets,
  colsByBreakpoint,
  getDefaultWidth,
  normalizeWidgets,
} from "../../services/dashboard/dashboard.mapper";

// ✅ on garde TES imports (à adapter au chemin réel de ton api.ts)
import {
  fetchDashboardWidgets,
  addWidget,
  updateDashboard,
  deleteWidget,
} from "../../services/dashboard/dashboard.api";

export type DashboardDisposition =
  | "auto"
  | "one"
  | "two"
  | "three"
  | "four"
  | "six"
  | "70-30"
  | "30-70"
  | "25-50-25"
  | "20-60-20";

export function useDashboard(id_dashboard: number) {
  const [layouts, setLayouts] = useState<Layouts>({});
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>("lg");
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saving" | "success" | "error" | "">("");

  const canSave = useMemo(() => widgets.length > 0, [widgets.length]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const raw = await fetchDashboardWidgets(id_dashboard);
        const normalized = normalizeWidgets(raw);
        const builtLayouts = buildLayoutsFromWidgets(normalized);

        if (!mounted) return;
        setWidgets(normalized);
        setLayouts(builtLayouts);
      } catch (e) {
        if (!mounted) return;
        setWidgets([]);
        setLayouts({});
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [id_dashboard]);

const onLayoutChange = (currentLayout: Layout[], allLayouts: Layouts) => {
  setLayouts(allLayouts);

  // ✅ Layout de référence = lg
  const baseLayout = allLayouts?.lg ?? currentLayout;

  setWidgets((prev) =>
    prev.map((w) => {
      const l = baseLayout.find((it) => it.i === w.id);
      if (!l) return w;

      return {
        ...w,
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
      };
    })
  );
};

const save = async () => {
  setSaveStatus("saving");
  try {
    const payload = widgets.map((w) => ({
      id_widget: w.id_widget,
      content: w.content ?? "",
      type: w.type ?? "",
      x: Number(w.x ?? 0),
      y: Number(w.y ?? 0),
      w: Number(w.w ?? 1),
      h: Number(w.h ?? 2),
    }));

    await updateDashboard(payload);
    setSaveStatus("success");
  } catch (e) {
    setSaveStatus("error");
  }
};

  const syncWidgetsAndLayouts = (nextWidgets: DashboardWidget[]) => {
    setWidgets(nextWidgets);
    setLayouts(buildLayoutsFromWidgets(nextWidgets));
  };

  const updateWidgetLayout = (
    widgetId: string,
    patch: Partial<Pick<DashboardWidget, "x" | "y" | "w" | "h">>
  ) => {
    setWidgets((prev) => {
      const nextWidgets = prev.map((widget) => {
        if (widget.id !== widgetId) return widget;

        const nextWidth = Math.max(1, Math.min(colsByBreakpoint.lg, Number(patch.w ?? widget.w)));
        const nextX = Math.max(0, Math.min(colsByBreakpoint.lg - nextWidth, Number(patch.x ?? widget.x)));

        return {
          ...widget,
          ...patch,
          x: nextX,
          y: Math.max(0, Number(patch.y ?? widget.y)),
          w: nextWidth,
          h: Math.max(2, Number(patch.h ?? widget.h)),
        };
      });

      setLayouts(buildLayoutsFromWidgets(nextWidgets));
      return nextWidgets;
    });
  };

  const updateWidgetContent = (
    widgetId: string,
    patch: Partial<Pick<DashboardWidget, "type" | "content">>
  ) => {
    setWidgets((prev) =>
      prev.map((widget) =>
        widget.id === widgetId
          ? {
              ...widget,
              ...patch,
            }
          : widget
      )
    );
  };

  const packWidgets = (items: DashboardWidget[]) => {
    let x = 0;
    let y = 0;
    let rowHeight = 0;

    return items.map((widget) => {
      const width = Math.max(1, Math.min(colsByBreakpoint.lg, Number(widget.w || 4)));
      const height = Math.max(2, Number(widget.h || 4));

      if (x + width > colsByBreakpoint.lg) {
        x = 0;
        y += rowHeight || 6;
        rowHeight = 0;
      }

      const next = {
        ...widget,
        x,
        y,
        w: width,
        h: height,
      };

      x += width;
      rowHeight = Math.max(rowHeight, height);
      return next;
    });
  };

  const moveWidget = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;

    const ordered = [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
    const source = ordered.find((widget) => widget.id === sourceId);
    if (!source) return;

    const withoutSource = ordered.filter((widget) => widget.id !== sourceId);
    const targetIndex = withoutSource.findIndex((widget) => widget.id === targetId);
    const insertIndex = targetIndex >= 0 ? targetIndex : withoutSource.length;
    withoutSource.splice(insertIndex, 0, source);

    syncWidgetsAndLayouts(packWidgets(withoutSource));
  };

  const applyDisposition = (disposition: DashboardDisposition) => {
    const patterns: Record<DashboardDisposition, number[]> = {
      auto: [],
      one: [12],
      two: [6, 6],
      three: [4, 4, 4],
      four: [3, 3, 3, 3],
      six: [2, 2, 2, 2, 2, 2],
      "70-30": [8, 4],
      "30-70": [4, 8],
      "25-50-25": [3, 6, 3],
      "20-60-20": [2, 8, 2],
    };

    const pattern = patterns[disposition];

    const nextWidgets = widgets.map((widget, index) => {
      if (!pattern.length) {
        const width = widget.type === "alerts" ? 8 : 4;
        return {
          ...widget,
          x: (index * width) % colsByBreakpoint.lg,
          y: Math.floor((index * width) / colsByBreakpoint.lg) * 8,
          w: width,
          h: widget.type === "fleet_counter" ? 4 : 8,
        };
      }

      const positionInRow = index % pattern.length;
      const width = pattern[positionInRow];
      const x = pattern.slice(0, positionInRow).reduce((sum, item) => sum + item, 0);

      return {
        ...widget,
        x,
        y: Math.floor(index / pattern.length) * 8,
        w: width,
        h: widget.type === "fleet_counter" ? 4 : 8,
      };
    });

    syncWidgetsAndLayouts(nextWidgets);
  };

  const addNewWidget = async (
    content: string,
    type: string,
    layout?: Partial<Pick<DashboardWidget, "x" | "y" | "w" | "h">>
  ) => {
    // même payload que ton handleAddWidget :contentReference[oaicite:17]{index=17}
    const widgetData: Omit<DashboardWidget, "id"> = {
      id_dashboard,
      id_widget: 0,
      id_user: 1, // TODO: remplacer par user connecté
      content,
      type,
      x: Number(layout?.x ?? 0),
      y: Number(layout?.y ?? 0),
      w: Number(layout?.w ?? getDefaultWidth("lg")),
      h: Number(layout?.h ?? 4),
    };

    const added = await addWidget(widgetData as any);
    const id_widget = added?.id_widget ?? (added as any)?.result?.insertId;
    const id = String(id_widget ?? "0");

    const newWidget: DashboardWidget = {
      id,
      ...widgetData,
      id_widget: Number(id_widget ?? 0),
    };

    setWidgets((prev) => [...prev, newWidget]);
    setLayouts((prev) => {
      const copy: Layouts = { ...prev };
      Object.keys(copy).forEach((bp) => {
        const list = copy[bp] ?? [];
        copy[bp] = [
          ...list,
          {
            i: id,
            x: bp === "lg" ? newWidget.x : 0,
            y: newWidget.y,
            w: bp === "lg" ? newWidget.w : getDefaultWidth(bp as Breakpoint),
            h: newWidget.h,
          },
        ];
      });
      return copy;
    });
  };




// ✅ SUPPRIMER (avec id_widget)
const removeWidget = async (id_widget: string | number) => {
  const removed = widgets.find((w) => w.id_widget === Number(id_widget));
  const removedGridId = removed?.id;

  // ✅ FIX TS2345 ICI
  await deleteWidget(String(id_widget));

  setWidgets((prev) =>
    prev.filter((w) => w.id_widget !== Number(id_widget))
  );

  if (!removedGridId) return;

  setLayouts((prev) => {
    const copy: Layouts = { ...prev };
    Object.keys(copy).forEach((bp) => {
      copy[bp] = (copy[bp] ?? []).filter((l) => l.i !== removedGridId);
    });
    return copy;
  });
};




  return {
    widgets,
    layouts,
    loading,
    saveStatus,
    canSave,
    currentBreakpoint,
    setCurrentBreakpoint,
    onLayoutChange,
    updateWidgetLayout,
    updateWidgetContent,
    moveWidget,
    applyDisposition,
    addNewWidget,
    removeWidget,
    save,
  };
}
